<template>
  <div data-test="velocity-chart" class="relative h-56">
    <div
      v-if="!hasData"
      class="absolute inset-0 flex items-center justify-center text-sm"
      style="color: var(--fg-muted)"
      data-test="velocity-empty"
    >
      Need 2+ closed sprints to chart velocity
    </div>
    <Line v-else :data="chartData" :options="chartOptions" />
  </div>
</template>

<script setup lang="ts">
import "@/lib/chartSetup";
import { computed } from "vue";
import { Line } from "vue-chartjs";
import type { ChartData, ChartOptions } from "chart.js";
import { cssVar } from "@/lib/chartTokens";
import { rollingAverage, type VelocityPoint } from "@/lib/velocity";

const props = withDefaults(
  defineProps<{
    series: VelocityPoint[];
    rollingWindow?: number;
  }>(),
  { rollingWindow: 3 },
);

const hasData = computed(() => {
  // At least 2 points where either planned or delivered is present.
  const points = props.series.filter((p) => p.planned != null || p.delivered != null);
  return points.length >= 2;
});

const labels = computed(() => props.series.map((p) => p.sprintName));

const plannedSeries = computed(() => props.series.map((p) => p.planned));
const deliveredSeries = computed(() => props.series.map((p) => p.delivered));
const rollingSeries = computed(() =>
  rollingAverage(deliveredSeries.value, props.rollingWindow),
);

const chartData = computed<ChartData<"line", Array<number | null>, string>>(() => {
  const accent = cssVar("--accent");
  const fgMuted = cssVar("--fg-muted");
  return {
    labels: labels.value,
    datasets: [
      {
        label: "Planned (M5)",
        data: plannedSeries.value,
        borderColor: fgMuted,
        backgroundColor: fgMuted,
        borderDash: [6, 4],
        borderWidth: 1.5,
        pointRadius: 2,
        tension: 0.25,
        spanGaps: true,
      },
      {
        label: "Delivered (M1)",
        data: deliveredSeries.value,
        borderColor: accent,
        backgroundColor: accent,
        borderWidth: 2,
        pointRadius: 3,
        tension: 0.25,
        spanGaps: true,
      },
      {
        label: `Rolling ${props.rollingWindow}-sprint avg`,
        data: rollingSeries.value,
        borderColor: accent,
        backgroundColor: "transparent",
        borderDash: [2, 3],
        borderWidth: 1.5,
        pointRadius: 0,
        tension: 0.4,
        spanGaps: true,
      },
    ],
  };
});

const chartOptions = computed<ChartOptions<"line">>(() => {
  const grid = cssVar("--grid");
  const fgMuted = cssVar("--fg-muted");
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: { color: fgMuted, boxWidth: 14, font: { size: 11 } },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const v = ctx.parsed.y;
            return `${ctx.dataset.label}: ${v == null ? "—" : v.toFixed(1)}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { color: fgMuted, font: { size: 10 } },
        grid: { color: grid },
      },
      y: {
        beginAtZero: true,
        ticks: { color: fgMuted, font: { size: 10 } },
        grid: { color: grid },
        title: { display: true, text: "Story points", color: fgMuted, font: { size: 11 } },
      },
    },
  };
});

defineExpose({ chartData, chartOptions, hasData });
</script>
